import { Flex, useColorModeValue } from '@chakra-ui/react';
import React from 'react';
import Select, {
  components,
  StylesConfig,
  ValueContainerProps,
} from 'react-select';

import { colors } from '../colors';
import { Icon } from './icons';

interface Options {
  readonly value: string;
  readonly label: string;
}

const ValueContainer: React.FC<ValueContainerProps<Options>> = ({
  children,
  ...props
}) => {
  return (
    components.ValueContainer && (
      <components.ValueContainer {...props}>
        <Flex alignItems="center" w="100%">
          <Icon
            icon="magnifying-glass"
            size="16px"
            color={colors.black[70]}
            style={{ margin: '0 16px' }}
          />
          {children}
        </Flex>
      </components.ValueContainer>
    )
  );
};

export const selectStyles = ({
  bgColor,
  borderColor,
  fontColor,
}: {
  bgColor: string;
  borderColor: string;
  fontColor: string;
}): StylesConfig<Options> => ({
  container: (styles) => ({
    ...styles,
    minWidth: '300px',
  }),
  control: (styles, { isDisabled }) => ({
    ...styles,
    backgroundColor: '#00000000',
    borderColor,
    color: '#ffffffeb',
    fontWeight: 600,
    borderRadius: '0.375rem',
    opacity: isDisabled ? '0.4' : 'none',
  }),
  menu: (styles) => ({ ...styles, backgroundColor: bgColor }),
  input: (styles) => ({ ...styles, color: fontColor }),
  option: (styles, { isFocused, isSelected, isDisabled }) => {
    let backgroundColor;
    if (isSelected) {
      backgroundColor = '#5367a0';
    } else {
      backgroundColor = isFocused ? '#ffffff0f' : '#00000000';
    }
    return {
      ...styles,
      backgroundColor: isDisabled ? undefined : backgroundColor,
      color: isDisabled ? 'grey' : fontColor,
      cursor: isDisabled ? 'not-allowed' : 'default',
    };
  },
  singleValue: (styles) => ({ ...styles, color: fontColor }),
});

export interface SearchSelectProps {
  value?: Options;
  onChange: (nextValue: string) => void;
  options: { label: string; value: string; isDisabled?: boolean }[];
  isDisabled?: boolean;
  isClearable?: boolean;
  isRightToLeft?: boolean;
  placeholder?: string;
  displaySearchIcon?: boolean;
}

export function SearchSelect({
  value,
  onChange,
  options,
  isDisabled = false,
  isClearable = false,
  isRightToLeft = false,
  placeholder,
  displaySearchIcon = false,
}: SearchSelectProps) {
  const bgColor = useColorModeValue(colors.background[50], '#394B59');
  const borderColor = useColorModeValue('gray.100', '#ffffff29');
  const fontColor = useColorModeValue('black', 'white');

  return (
    <Select
      value={value}
      isDisabled={isDisabled}
      isClearable={isClearable}
      isRtl={isRightToLeft}
      isSearchable
      placeholder={placeholder}
      onChange={(newValue) => {
        const newVal = newValue as { value: string; label: string };
        onChange(newVal.value);
      }}
      options={options}
      components={displaySearchIcon ? { ValueContainer } : undefined}
      styles={selectStyles({ bgColor, borderColor, fontColor })}
    />
  );
}
